import {SomeCountryCode} from '~/entities/countries';
import {defineFormStateValidation, Issues} from '~/library/form-models.v1';
import {define} from '../lib/define';
import {VASP} from './VaspPicker';
import {UserInfoResource} from './useUserInfoResource';

const t = {
    Required: 'This field is required',
    MaxParticipantName: `Should have ${140} characters or less`,
    Email: 'Must be a valid email',
    NotEnough: 'Not enough funds',
};

export type TxParticipantType = (typeof TxParticipantType)[keyof typeof TxParticipantType];
export const TxParticipantType = {
    self: 'own_wallet',
    individual: 'third_party_individual',
    company: 'third_party_company',
} as const;

export type WalletType = 'vasp' | 'unhosted';

export type TrTxParticipantValues = ReturnType<typeof TrTxParticipantValues>;
export const TrTxParticipantValues = () => ({
    walletVasp: define.nullable<VASP>(),
    walletType: define<WalletType>('vasp'),
    participantType: define<TxParticipantType>(TxParticipantType.individual),
    participantName: '',
    participantCountry: define.nullable<SomeCountryCode>(),
    participantTown: '',
    participantAddress: '',
});
type Values = TrTxParticipantValues;

export type OriginatorValues = ReturnType<typeof OriginatorValues>;
export const OriginatorValues = () => ({
    ...TrTxParticipantValues(),
});

const validateTrTxParticipantBase = (values: Values) => {
    const issues: Issues<Values> = {};
    if (values.participantType !== TxParticipantType.self) {
        issues.participantName = validateParticipantName(values.participantName);
    }
    if (values.walletType === 'vasp' && values.walletVasp === null) {
        issues.walletVasp = t.Required;
    }
    if (values.participantCountry == null) {
        issues.participantCountry = t.Required;
    }
    if (!values.participantTown) {
        issues.participantTown = t.Required;
    }
    if (!values.participantAddress) {
        issues.participantAddress = t.Required;
    }
    return {issues, values};
};

export const validateTrTxParticipant = defineFormStateValidation((values: Values) => {
    const participant = validateTrTxParticipantBase(values);
    const issues: Issues<OriginatorValues> = {};

    return {values, issues: {...issues, ...participant.issues}};
});

export const validateTrTxOriginator = defineFormStateValidation((values: OriginatorValues) => {
    const participant = validateTrTxParticipantBase(values);
    const issues: Issues<OriginatorValues> = {};

    return {values, issues: {...issues, ...participant.issues}};
});

export function validateParticipantName(name: string) {
    if (name === '') {
        return t.Required;
    }
    if (name.length > 140) {
        return t.MaxParticipantName;
    }
    return undefined;
}

export function participantIsCompany(values: {participantType: TxParticipantType}, user: UserInfoResource): boolean {
    return (
        values.participantType === TxParticipantType.company ||
        (values.participantType === TxParticipantType.self && user.type === 'company')
    );
}

export function getParticipantFieldsFromUser(user: UserInfoResource) {
    const userFields: Partial<Values> = {
        participantCountry: user.countryCode,
        participantName: user.fullName,
        participantTown: user.city,
        participantAddress: user.address,
    };
    return userFields;
}
